import React, { useMemo, useRef, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { wellbeingContentApi } from "../api/wellbeingContent";
import type { WellbeingCarouselItem, WellbeingFact } from "../api/wellbeingContent";
import { useSessionQuery } from "../../../auth";
import { qk } from "../../../../lib/queryKeys";

const CATEGORY_OPTIONS = [
  "Sleep",
  "Mind",
  "Hydration",
  "Movement",
  "Nutrition",
  "Recovery",
];

type FactFormState = {
  id?: string;
  title: string;
  body: string;
  category: string;
  source: string;
  is_active: boolean;
  starts_on: string;
  ends_on: string;
  priority: number;
};

type CarouselFormState = {
  id?: string;
  title: string;
  body: string;
  media_type: "image" | "video";
  media_url: string;
  thumbnail_url: string;
  tag: string;
  action_label: string;
  action_url: string;
  is_active: boolean;
  starts_on: string;
  ends_on: string;
  priority: number;
};

const emptyFactForm: FactFormState = {
  title: "",
  body: "",
  category: "",
  source: "",
  is_active: true,
  starts_on: "",
  ends_on: "",
  priority: 0,
};

const emptyCarouselForm: CarouselFormState = {
  title: "",
  body: "",
  media_type: "image",
  media_url: "",
  thumbnail_url: "",
  tag: "",
  action_label: "",
  action_url: "",
  is_active: true,
  starts_on: "",
  ends_on: "",
  priority: 0,
};

const BODY_CLAMP_LINES = 3;

const ExpandableBody: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded((prev) => !prev), []);

  // Estimate whether text is likely to overflow 3 lines (~~80 chars per line).
  const likelyOverflows = text.length > 240 || text.includes("\n");

  return (
    <Box>
      <Typography
        variant="body2"
        sx={
          !expanded && likelyOverflows
            ? {
                display: "-webkit-box",
                WebkitLineClamp: BODY_CLAMP_LINES,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                whiteSpace: "pre-wrap",
              }
            : { whiteSpace: "pre-wrap" }
        }
      >
        {text}
      </Typography>
      {likelyOverflows && (
        <Button
          size="small"
          variant="text"
          sx={{ mt: 0.5, p: 0, minWidth: 0, fontSize: "0.75rem" }}
          onClick={toggle}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </Box>
  );
};

const toDateInput = (value?: string | null) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const mapFactToForm = (fact: WellbeingFact): FactFormState => ({
  id: fact.id,
  title: fact.title ?? "",
  body: fact.body ?? "",
  category: fact.category ?? "",
  source: fact.source ?? "",
  is_active: fact.isActive ?? true,
  starts_on: toDateInput(fact.startsOn ?? null),
  ends_on: toDateInput(fact.endsOn ?? null),
  priority: fact.priority ?? 0,
});

const mapCarouselToForm = (item: WellbeingCarouselItem): CarouselFormState => ({
  id: item.id,
  title: item.title ?? "",
  body: item.body ?? "",
  media_type: item.mediaType === "video" ? "video" : "image",
  media_url: item.mediaUrl ?? item.imageUrl ?? "",
  thumbnail_url: item.imageUrl ?? "",
  tag: item.tag ?? "",
  action_label: item.actionLabel ?? "",
  action_url: item.actionUrl ?? "",
  is_active: item.isActive ?? true,
  starts_on: toDateInput(item.startsOn ?? null),
  ends_on: toDateInput(item.endsOn ?? null),
  priority: item.priority ?? 0,
});

const validateDateRange = (start: string, end: string) => {
  if (!start || !end) return null;
  if (start > end) return "Start date must be before end date.";
  return null;
};

const countWords = (value: string) =>
  value
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

export const WellbeingContent: React.FC = () => {
  const { data: session } = useSessionQuery();
  const isCentralAdmin = session?.user?.is_central_admin ?? false;
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("facts");
  const [factForm, setFactForm] = useState<FactFormState>(emptyFactForm);
  const [carouselForm, setCarouselForm] = useState<CarouselFormState>(emptyCarouselForm);
  const [factDialogOpen, setFactDialogOpen] = useState(false);
  const [carouselDialogOpen, setCarouselDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const thumbInputRef = useRef<HTMLInputElement | null>(null);

  const factsQuery = useQuery({
    queryKey: qk.wellbeingFacts(),
    queryFn: () => wellbeingContentApi.listFacts(),
    enabled: isCentralAdmin,
  });

  const carouselQuery = useQuery({
    queryKey: qk.wellbeingCarousel(),
    queryFn: () => wellbeingContentApi.listCarousel(),
    enabled: isCentralAdmin,
  });

  const saveFactMutation = useMutation({
    mutationFn: (payload: FactFormState) =>
      payload.id
        ? wellbeingContentApi.updateFact(payload.id, {
            title: payload.title,
            body: payload.body,
            category: payload.category || null,
            source: payload.source || null,
            is_active: payload.is_active,
            starts_on: payload.starts_on || null,
            ends_on: payload.ends_on || null,
            priority: payload.priority,
          })
        : wellbeingContentApi.createFact({
            title: payload.title,
            body: payload.body,
            category: payload.category || null,
            source: payload.source || null,
            is_active: payload.is_active,
            starts_on: payload.starts_on || null,
            ends_on: payload.ends_on || null,
            priority: payload.priority,
          }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.wellbeingFacts() });
      setFactDialogOpen(false);
      setFactForm(emptyFactForm);
    },
  });

  const deactivateFactMutation = useMutation({
    mutationFn: (id: string) => wellbeingContentApi.deactivateFact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.wellbeingFacts() });
    },
  });

  const saveCarouselMutation = useMutation({
    mutationFn: (payload: CarouselFormState) =>
      payload.id
        ? wellbeingContentApi.updateCarousel(payload.id, {
            title: payload.title,
            body: payload.body || null,
            image_url:
              payload.media_type === "video"
                ? payload.thumbnail_url || null
                : payload.media_url || null,
            media_url: payload.media_url || null,
            media_type: payload.media_type,
            tag: payload.tag || null,
            action_label: payload.action_label || null,
            action_url: payload.action_url || null,
            is_active: payload.is_active,
            starts_on: payload.starts_on || null,
            ends_on: payload.ends_on || null,
            priority: payload.priority,
          })
        : wellbeingContentApi.createCarousel({
            title: payload.title,
            body: payload.body || null,
            image_url:
              payload.media_type === "video"
                ? payload.thumbnail_url || null
                : payload.media_url || null,
            media_url: payload.media_url || null,
            media_type: payload.media_type,
            tag: payload.tag || null,
            action_label: payload.action_label || null,
            action_url: payload.action_url || null,
            is_active: payload.is_active,
            starts_on: payload.starts_on || null,
            ends_on: payload.ends_on || null,
            priority: payload.priority,
          }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.wellbeingCarousel() });
      setCarouselDialogOpen(false);
      setCarouselForm(emptyCarouselForm);
    },
  });

  const deleteCarouselMutation = useMutation({
    mutationFn: (id: string) => wellbeingContentApi.deleteCarousel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.wellbeingCarousel() });
    },
  });

  const factErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!factForm.title.trim()) errors.title = "Title is required.";
    if (!factForm.body.trim()) errors.body = "Body is required.";
    const dateError = validateDateRange(factForm.starts_on, factForm.ends_on);
    if (dateError) errors.date = dateError;
    return errors;
  }, [factForm]);

  const factBodyWordCount = useMemo(
    () => countWords(factForm.body),
    [factForm.body]
  );

  const carouselErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!carouselForm.title.trim()) errors.title = "Title is required.";
    const dateError = validateDateRange(
      carouselForm.starts_on,
      carouselForm.ends_on,
    );
    if (dateError) errors.date = dateError;
    if (
      (carouselForm.action_label && !carouselForm.action_url) ||
      (!carouselForm.action_label && carouselForm.action_url)
    ) {
      errors.action = "Action label and URL must both be set.";
    }
    if (carouselForm.media_type === "video" && !carouselForm.thumbnail_url.trim()) {
      errors.thumbnail = "Thumbnail is required for video.";
    }
    return errors;
  }, [carouselForm]);

  const onUploadMedia = async (file: File, field: "media_url" | "thumbnail_url") => {
    setUploading(true);
    setUploadError(null);
    try {
      const { upload_url, file_url } = await wellbeingContentApi.createCarouselUploadUrl({
        filename: file.name,
        content_type: file.type || "application/octet-stream",
      });
      await fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      setCarouselForm((prev) => ({ ...prev, [field]: file_url }));
    } catch (error) {
      console.error("Carousel media upload failed", error);
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (!isCentralAdmin) {
    return (
      <Alert severity="warning">
        Central admin access required to manage wellbeing content.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Wellbeing Content
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage facts of the day and home carousel content for the mobile app.
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          aria-label="wellbeing content tabs"
        >
          <Tab value="facts" label="Facts" />
          <Tab value="carousel" label="Carousel" />
        </Tabs>

        {activeTab === "facts" && (
          <Stack spacing={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => {
                  setFactForm(emptyFactForm);
                  setFactDialogOpen(true);
                }}
              >
                New fact
              </Button>
            </Box>

            {factsQuery.isLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading facts...</Typography>
              </Box>
            ) : factsQuery.error ? (
              <Alert severity="error">Failed to load facts.</Alert>
            ) : factsQuery.data?.length ? (
              <Stack spacing={2}>
                {factsQuery.data.map((fact) => (
                  <Card key={fact.id} variant="outlined">
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="h6" fontWeight={600}>
                              {fact.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {fact.category || "Uncategorized"}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setFactForm(mapFactToForm(fact));
                                setFactDialogOpen(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="text"
                              color="error"
                              size="small"
                              onClick={() => deactivateFactMutation.mutate(fact.id)}
                            >
                              Deactivate
                            </Button>
                          </Stack>
                        </Stack>
                        <Typography variant="body2">{fact.body}</Typography>
                        <Divider />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <Typography variant="caption" color="text.secondary">
                            Active: {fact.isActive ? "Yes" : "No"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Priority: {fact.priority ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Starts: {fact.startsOn ? toDateInput(fact.startsOn) : "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ends: {fact.endsOn ? toDateInput(fact.endsOn) : "-"}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">No facts found.</Alert>
            )}
          </Stack>
        )}

        {activeTab === "carousel" && (
          <Stack spacing={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => {
                  setCarouselForm(emptyCarouselForm);
                  setCarouselDialogOpen(true);
                }}
              >
                New carousel item
              </Button>
            </Box>

            {deleteCarouselMutation.isError ? (
              <Alert severity="error">Failed to delete carousel item.</Alert>
            ) : null}

            {carouselQuery.isLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading carousel...</Typography>
              </Box>
            ) : carouselQuery.error ? (
              <Alert severity="error">Failed to load carousel.</Alert>
            ) : carouselQuery.data?.length ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {carouselQuery.data.map((item) => (
                  <Card key={item.id} variant="outlined">
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="h6" fontWeight={600}>
                              {item.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.tag || "No tag"}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setCarouselForm(mapCarouselToForm(item));
                                setCarouselDialogOpen(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              disabled={deleteCarouselMutation.isPending}
                              onClick={() => {
                                if (
                                  !window.confirm(
                                    `Delete carousel item \"${item.title}\"?`
                                  )
                                ) {
                                  return;
                                }
                                deleteCarouselMutation.mutate(item.id);
                              }}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Stack>
                        {item.mediaType === "video" && item.mediaUrl ? (
                          <Box
                            component="video"
                            src={item.mediaUrl}
                            controls
                            sx={{
                              width: "100%",
                              maxHeight: 240,
                              borderRadius: 1,
                            }}
                          />
                        ) : item.mediaUrl || item.imageUrl ? (
                          <Box
                            component="img"
                            src={item.mediaUrl ?? item.imageUrl ?? ""}
                            alt={item.title}
                            sx={{
                              width: "100%",
                              maxHeight: 200,
                              objectFit: "cover",
                              borderRadius: 1,
                            }}
                          />
                        ) : null}
                        {item.body ? (
                          <ExpandableBody text={item.body} />
                        ) : null}
                        <Divider />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <Typography variant="caption" color="text.secondary">
                            Active: {item.isActive ? "Yes" : "No"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Priority: {item.priority ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Starts: {item.startsOn ? toDateInput(item.startsOn) : "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ends: {item.endsOn ? toDateInput(item.endsOn) : "-"}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Alert severity="info">No carousel items found.</Alert>
            )}
          </Stack>
        )}
      </Stack>

      <Dialog
        open={factDialogOpen}
        onClose={() => setFactDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{factForm.id ? "Edit fact" : "Create fact"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {saveFactMutation.isError ? (
              <Alert severity="error">Failed to save fact.</Alert>
            ) : null}
            <TextField
              label="Title"
              value={factForm.title}
              onChange={(event) =>
                setFactForm((prev) => ({ ...prev, title: event.target.value }))
              }
              error={Boolean(factErrors.title)}
              helperText={factErrors.title}
              fullWidth
            />
            <TextField
              label="Body"
              value={factForm.body}
              onChange={(event) =>
                setFactForm((prev) => ({ ...prev, body: event.target.value }))
              }
              error={Boolean(factErrors.body)}
              helperText={factErrors.body}
              fullWidth
              multiline
              minRows={3}
            />
            <Typography variant="caption" color="text.secondary">
              {factBodyWordCount} words, {factForm.body.length} characters
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  Preview
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {factForm.title.trim() || "Untitled fact"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {factForm.body.trim() || "Fact body preview will appear here."}
                </Typography>
              </CardContent>
            </Card>
            <TextField
              label="Category"
              value={factForm.category}
              onChange={(event) =>
                setFactForm((prev) => ({ ...prev, category: event.target.value }))
              }
              fullWidth
              select
              SelectProps={{ native: true }}
            >
              <option value="">Select a category</option>
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>
            <TextField
              label="Source"
              value={factForm.source}
              onChange={(event) =>
                setFactForm((prev) => ({ ...prev, source: event.target.value }))
              }
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Starts on"
                type="date"
                value={factForm.starts_on}
                onChange={(event) =>
                  setFactForm((prev) => ({
                    ...prev,
                    starts_on: event.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Ends on"
                type="date"
                value={factForm.ends_on}
                onChange={(event) =>
                  setFactForm((prev) => ({
                    ...prev,
                    ends_on: event.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            {factErrors.date ? (
              <Alert severity="warning">{factErrors.date}</Alert>
            ) : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Priority"
                type="number"
                value={factForm.priority}
                onChange={(event) =>
                  setFactForm((prev) => ({
                    ...prev,
                    priority: Number(event.target.value),
                  }))
                }
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={factForm.is_active}
                    onChange={(event) =>
                      setFactForm((prev) => ({
                        ...prev,
                        is_active: event.target.checked,
                      }))
                    }
                  />
                }
                label="Active"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFactDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              Boolean(Object.keys(factErrors).length) || saveFactMutation.isPending
            }
            onClick={() => saveFactMutation.mutate(factForm)}
          >
            {saveFactMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={carouselDialogOpen}
        onClose={() => setCarouselDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {carouselForm.id ? "Edit carousel item" : "Create carousel item"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {saveCarouselMutation.isError ? (
              <Alert severity="error">Failed to save carousel item.</Alert>
            ) : null}
            <TextField
              label="Title"
              value={carouselForm.title}
              onChange={(event) =>
                setCarouselForm((prev) => ({
                  ...prev,
                  title: event.target.value,
                }))
              }
              error={Boolean(carouselErrors.title)}
              helperText={carouselErrors.title}
              fullWidth
            />
            <TextField
              label="Body"
              value={carouselForm.body}
              onChange={(event) =>
                setCarouselForm((prev) => ({
                  ...prev,
                  body: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={4}
              maxRows={12}
              helperText={`${carouselForm.body.length} characters`}
            />
            <Stack spacing={1.5}>
              <Alert severity="info">
                Recommended media specs:
                <br />
                Image: 1600 x 900 (16:9), JPG/PNG/WebP, ideally under 2MB.
                <br />
                Video: MP4 (H.264), 1280 x 720 or 1920 x 1080 (16:9), ideally under
                50MB.
                <br />
                Video thumbnail: 1600 x 900 (16:9), JPG/PNG/WebP, ideally under 1MB.
              </Alert>
              <TextField
                select
                label="Media type"
                value={carouselForm.media_type}
                onChange={(event) =>
                  setCarouselForm((prev) => ({
                    ...prev,
                    media_type: event.target.value as "image" | "video",
                  }))
                }
                fullWidth
              >
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="video">Video</MenuItem>
              </TextField>
              <TextField
                label={carouselForm.media_type === "video" ? "Video URL" : "Image URL"}
                value={carouselForm.media_url}
                onChange={(event) =>
                  setCarouselForm((prev) => ({
                    ...prev,
                    media_url: event.target.value,
                  }))
                }
                helperText={
                  carouselForm.media_type === "video"
                    ? "Recommended: MP4 video, 16:9 (1280x720 or 1920x1080)."
                    : "Recommended: 16:9 image, 1600x900."
                }
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading
                    ? "Uploading..."
                    : carouselForm.media_type === "video"
                      ? "Upload video"
                      : "Upload image"}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={carouselForm.media_type === "video" ? "video/*" : "image/*"}
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void onUploadMedia(file, "media_url");
                    if (event.target) event.target.value = "";
                  }}
                />
              </Stack>
              {carouselForm.media_type === "video" ? (
                <>
                  <TextField
                    label="Thumbnail URL"
                    value={carouselForm.thumbnail_url}
                    onChange={(event) =>
                      setCarouselForm((prev) => ({
                        ...prev,
                        thumbnail_url: event.target.value,
                      }))
                    }
                    error={Boolean(carouselErrors.thumbnail)}
                    helperText={
                      carouselErrors.thumbnail ||
                      "Recommended: 16:9 image, 1600x900."
                    }
                    fullWidth
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Button
                      variant="outlined"
                      onClick={() => thumbInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? "Uploading..." : "Upload thumbnail"}
                    </Button>
                    <input
                      ref={thumbInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) void onUploadMedia(file, "thumbnail_url");
                        if (event.target) event.target.value = "";
                      }}
                    />
                  </Stack>
                </>
              ) : null}
              {carouselForm.media_type === "video" && carouselForm.media_url ? (
                <Box
                  component="video"
                  src={carouselForm.media_url}
                  controls
                  sx={{
                    width: "100%",
                    maxHeight: 240,
                    borderRadius: 1,
                  }}
                />
              ) : carouselForm.media_url ? (
                <Box
                  component="img"
                  src={carouselForm.media_url}
                  alt="Carousel preview"
                  sx={{
                    width: "100%",
                    maxHeight: 200,
                    objectFit: "cover",
                    borderRadius: 1,
                  }}
                />
              ) : null}
              {carouselForm.media_type === "video" && carouselForm.thumbnail_url ? (
                <Box
                  component="img"
                  src={carouselForm.thumbnail_url}
                  alt="Thumbnail preview"
                  sx={{
                    width: "100%",
                    maxHeight: 160,
                    objectFit: "cover",
                    borderRadius: 1,
                  }}
                />
              ) : null}
              {uploadError ? (
                <Alert severity="error">{uploadError}</Alert>
              ) : null}
            </Stack>
            <TextField
              label="Tag"
              value={carouselForm.tag}
              onChange={(event) =>
                setCarouselForm((prev) => ({
                  ...prev,
                  tag: event.target.value,
                }))
              }
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Action label"
                value={carouselForm.action_label}
                onChange={(event) =>
                  setCarouselForm((prev) => ({
                    ...prev,
                    action_label: event.target.value,
                  }))
                }
                fullWidth
              />
              <TextField
                label="Action URL"
                value={carouselForm.action_url}
                onChange={(event) =>
                  setCarouselForm((prev) => ({
                    ...prev,
                    action_url: event.target.value,
                  }))
                }
                fullWidth
              />
            </Stack>
            {carouselErrors.action ? (
              <Alert severity="warning">{carouselErrors.action}</Alert>
            ) : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Starts on"
                type="date"
                value={carouselForm.starts_on}
                onChange={(event) =>
                  setCarouselForm((prev) => ({
                    ...prev,
                    starts_on: event.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Ends on"
                type="date"
                value={carouselForm.ends_on}
                onChange={(event) =>
                  setCarouselForm((prev) => ({
                    ...prev,
                    ends_on: event.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            {carouselErrors.date ? (
              <Alert severity="warning">{carouselErrors.date}</Alert>
            ) : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Priority"
                type="number"
                value={carouselForm.priority}
                onChange={(event) =>
                  setCarouselForm((prev) => ({
                    ...prev,
                    priority: Number(event.target.value),
                  }))
                }
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={carouselForm.is_active}
                    onChange={(event) =>
                      setCarouselForm((prev) => ({
                        ...prev,
                        is_active: event.target.checked,
                      }))
                    }
                  />
                }
                label="Active"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCarouselDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              Boolean(Object.keys(carouselErrors).length) ||
              saveCarouselMutation.isPending ||
              uploading
            }
            onClick={() => saveCarouselMutation.mutate(carouselForm)}
          >
            {saveCarouselMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
